import { useState, useCallback } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import posthog from 'posthog-js';

export type ToolStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'error';

interface ToolBgRemovalState {
  status: ToolStatus;
  projectId: string | null;
  processedVideoUrl: string | null;
  originalVideoUrl: string | null;
  processedStorageId: string | null;
  videoStorageId: string | null;
  error: string | null;
}

async function computeFileHash(file: File | Blob): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex.substring(0, 32);
}

export function useToolBgRemoval() {
  const [state, setState] = useState<ToolBgRemovalState>({
    status: 'idle',
    projectId: null,
    processedVideoUrl: null,
    originalVideoUrl: null,
    processedStorageId: null,
    videoStorageId: null,
    error: null,
  });

  const generateUploadUrl = useMutation(api.projects.generateUploadUrl);
  const toolRemoveBackground = useAction(api.projectsActions.toolRemoveBackground);

  const processFile = useCallback(async (file: File) => {
    setState(prev => ({ ...prev, status: 'uploading', error: null }));
    posthog.capture('tool_processing_started');

    try {
      // 1. Compute file hash
      const fileHash = await computeFileHash(file);

      // 2. Upload to Convex storage
      const uploadUrl = await generateUploadUrl();
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status}`);
      }

      const uploadResult = await uploadResponse.json();
      const storageId = uploadResult.storageId || uploadResult.id;
      if (!storageId) {
        throw new Error('Upload response missing storageId');
      }

      setState(prev => ({ ...prev, status: 'processing', videoStorageId: storageId }));

      // 3. Trigger bg removal action
      const result = await toolRemoveBackground({
        videoStorageId: storageId as Id<'_storage'>,
        fileHash,
        videoFileName: file.name,
      });

      if (result.status === 'completed' && result.processedVideoUrl) {
        posthog.capture('tool_completed');
        setState(prev => ({
          ...prev,
          status: 'completed',
          projectId: result.projectId,
          processedVideoUrl: result.processedVideoUrl!,
          originalVideoUrl: result.originalVideoUrl!,
          processedStorageId: result.processedStorageId!,
          videoStorageId: storageId,
        }));
      } else {
        throw new Error(result.error || 'Background removal failed');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      posthog.capture('tool_error', { error: errorMsg });
      setState(prev => ({ ...prev, status: 'error', error: errorMsg }));
    }
  }, [generateUploadUrl, toolRemoveBackground]);

  const reset = useCallback(() => {
    setState({
      status: 'idle',
      projectId: null,
      processedVideoUrl: null,
      originalVideoUrl: null,
      processedStorageId: null,
      videoStorageId: null,
      error: null,
    });
  }, []);

  return {
    ...state,
    processFile,
    reset,
  };
}
